import type { ExamItemCreateViewModel } from '@/shared/api/generated/types';

import type { QuestionAlternative } from '../hooks/useQuestionEdit';

/**
 * Estado atual do formulário usado como fallback quando a sugestão da IA
 * não trouxer determinado campo.
 */
export interface AiReviewCurrent {
  readonly label: string;
  readonly information: string;
  /** Resposta-modelo atual (subtipos ModelAnswerEssay/MbaCase). */
  readonly modelAnswer: string;
  /** Termos-chave atuais (subtipo ModelAnswerEssay). */
  readonly keyTerms: string;
  readonly alternatives: readonly QuestionAlternative[];
  readonly correctAlternativeIds: readonly string[];
  readonly isChoiceType: boolean;
}

/**
 * Resultado da tradução da sugestão da IA de volta para os campos do formulário.
 */
export interface AiReviewPatch {
  readonly label: string;
  readonly information: string;
  readonly modelAnswer: string;
  readonly keyTerms: string;
  readonly alternatives: QuestionAlternative[];
  readonly correctAlternativeIds: string[];
}

/**
 * Traduz a sugestão retornada pelo endpoint (`ExamItemCreateViewModel`) para o
 * formato dos campos do formulário de questão, aplicando fallback ao valor atual
 * quando a IA não sugerir um campo.
 *
 * As alternativas só são substituídas para tipos de escolha e quando a resposta
 * trouxer `examTypeList.examTypeListValues`; caso contrário, mantém as atuais.
 *
 * @param result - Sugestão retornada pela IA
 * @param current - Valores atuais do formulário (fallback)
 * @returns Patch com os novos valores a aplicar no formulário
 *
 * @example
 * ```ts
 * const patch = applyAiReviewToForm(result, current);
 * form.setValue('label', patch.label);
 * ```
 */
export function applyAiReviewToForm(
  result: ExamItemCreateViewModel,
  current: AiReviewCurrent,
): AiReviewPatch {
  const suggestedValues = result.examTypeList?.examTypeListValues;
  const shouldReplaceAlternatives =
    current.isChoiceType && Array.isArray(suggestedValues) && suggestedValues.length > 0;

  if (!shouldReplaceAlternatives) {
    return {
      label: result.label ?? current.label,
      information: result.information ?? current.information,
      modelAnswer: result.modelAnswer ?? current.modelAnswer,
      keyTerms: result.keyTerms ?? current.keyTerms,
      alternatives: [...current.alternatives],
      correctAlternativeIds: [...current.correctAlternativeIds],
    };
  }

  const alternatives: QuestionAlternative[] = suggestedValues.map((value, index) => ({
    id: String(index),
    ...(value.id != null ? { originalId: value.id } : {}),
    value: value.value ?? '',
    isCorrect: value.correctAnswer ?? false,
  }));

  return {
    label: result.label ?? current.label,
    information: result.information ?? current.information,
    modelAnswer: result.modelAnswer ?? current.modelAnswer,
    keyTerms: result.keyTerms ?? current.keyTerms,
    alternatives,
    correctAlternativeIds: alternatives.filter(alt => alt.isCorrect).map(alt => alt.id),
  };
}
